import * as React from 'react';
import { useId } from '@sail/react-aria';
import { warningCircle } from '@sail/icons/react/Icon';
import { createView, createViewConfig, extractRefFromProps, view } from '@sail/engine';
import { Icon } from './Icon';
import { useInputId, LABEL_OPT_OUT_STRING } from './hooks/useInputId';
import { Label } from './Label';
import { Skeleton } from './Skeleton';
import { visuallyHidden } from '../utilStyles/visuallyHidden';
import { css } from '../css';
import { useFormRegister } from './form/useFormRegister';
export const FormFieldMetadataContext = /*#__PURE__*/ React.createContext({
    controlId: '',
    elements: {
        description: {},
        label: {},
        error: {}
    }
});
export const ErrorText = createView(({ children, ...props })=>{
    return /*#__PURE__*/ React.createElement(view.div, props, /*#__PURE__*/ React.createElement(Icon, {
        icon: warningCircle,
        size: "xsmall"
    }), /*#__PURE__*/ React.createElement(view.div, {
        css: {
            width: 'fill',
            alignSelfX: 'start',
            boxSizing: 'border-box'
        }
    }, children));
}, {
    css: {
        stack: 'x',
        gap: 'xsmall',
        color: 'feedback.critical',
        font: 'label.small'
    },
    variants: {
        hidden: {
            true: visuallyHidden
        }
    },
    defaults: {
        hidden: false
    }
});
export const Description = createView('div', {
    css: {
        color: 'subdued',
        font: 'label.small'
    },
    variants: {
        hidden: {
            true: visuallyHidden
        }
    },
    defaults: {
        hidden: false
    }
});
// this is what users should be customizing
export const FormFieldConfig = createViewConfig({
    props: {},
    name: 'FormField',
    defaults: {
        isInCompositeField: false,
        layout: 'vertical'
    }
});
/**
 * A low-level component for building custom form inputs.
 */ export const FormField = FormFieldConfig.createView(({ label, description, error, hiddenElements = [], id: propsId, children, isInCompositeField, subviews, ...props })=>{
    const ref = React.useRef(null);
    const { id } = useInputId({
        label,
        outerRef: ref,
        id: propsId
    });
    const descriptionId = useId();
    const errorId = useId();
    const labelId = useId();
    const shouldRenderLabel = Boolean(label && label !== LABEL_OPT_OUT_STRING);
    const shouldHideLabel = isInCompositeField || hiddenElements.includes('label');
    const shouldHideDescription = isInCompositeField || hiddenElements.includes('description');
    const shouldHideError = isInCompositeField || hiddenElements.includes('error');
    return /*#__PURE__*/ React.createElement(FormFieldMetadataContext.Provider, {
        value: {
            elements: {
                description: {
                    id: descriptionId,
                    present: Boolean(description)
                },
                label: {
                    id: labelId,
                    present: shouldRenderLabel
                },
                error: {
                    id: errorId,
                    present: Boolean(error)
                }
            },
            controlId: id
        }
    }, /*#__PURE__*/ React.createElement(view.div, {
        ...props,
        ref: ref
    }, shouldRenderLabel ? /*#__PURE__*/ React.createElement(Label, {
        hidden: shouldHideLabel,
        htmlFor: id,
        id: labelId,
        inherits: subviews.label
    }, label) : null, description ? /*#__PURE__*/ React.createElement(Description, {
        hidden: shouldHideDescription,
        id: descriptionId,
        inherits: subviews.description
    }, description) : null, /*#__PURE__*/ React.createElement(view.div, {
        inherits: subviews.inputs
    }, children), error ? /*#__PURE__*/ React.createElement(ErrorText, {
        hidden: shouldHideError,
        id: errorId,
        inherits: subviews.error
    }, error) : null));
}, {
    css: {
        gapX: 'small',
        gapY: 'xsmall',
        font: 'label.medium',
        display: 'grid',
        alignItems: 'baseline'
    },
    subviews: {
        label: {
            css: {
                width: 'fit'
            }
        },
        description: {
            css: {
                bleedTop: 'xsmall'
            }
        }
    },
    variants: {
        disabled: {
            true: {
                subviews: {
                    label: {
                        css: {
                            color: 'form.disabled'
                        }
                    },
                    description: {
                        css: {
                            color: 'form.disabled'
                        }
                    }
                }
            }
        },
        layout: {
            horizontal: {
                css: {
                    gapX: 'space.0'
                },
                subviews: {
                    inputs: {
                        css: {
                            baselineAlign: 'auto'
                        },
                        uses: [
                            css({
                                gridArea: '1 / 1'
                            })
                        ]
                    },
                    label: {
                        uses: [
                            css({
                                gridColumn: '2',
                                paddingLeft: 'small'
                            })
                        ]
                    },
                    description: {
                        uses: [
                            css({
                                gridColumn: '2',
                                paddingLeft: 'small'
                            })
                        ]
                    },
                    error: {
                        uses: [
                            css({
                                gridColumn: '2',
                                paddingLeft: 'small'
                            })
                        ]
                    }
                },
                uses: [
                    css({
                        gridTemplateColumns: 'max-content 1fr'
                    })
                ]
            },
            vertical: css({
                gridTemplateColumns: '1fr'
            })
        }
    }
});
/**
 * A hook that separates props for the form field from props for the control.
 *
 * Splits props like `label`, `description` and `error` from the control props.
 * The control props are enhanced with form state when used within a Sail Form.
 */ export function useFormFieldProps(props, formOptions) {
    const [ref] = extractRefFromProps(props);
    const { description, disabled, error: errorProp, hiddenElements, inherits, label, outerRef, subviews, ...rest } = props;
    const controlProps = {
        ref,
        disabled,
        ...rest
    };
    const { componentProps, isInSailForm } = useFormRegister({
        props: controlProps,
        ...formOptions
    });
    const formError = isInSailForm && 'error' in componentProps ? componentProps.error : undefined;
    const fieldProps = {
        description,
        disabled: componentProps.disabled,
        error: errorProp || formError,
        hiddenElements,
        // TODO(koop): Should we still forward the id prop to the control?
        // The useFieldContexts hook will retrieve the controlId from the context
        // set by the wrapper FormField component. The current logic is safe and
        // doesn't result in duplicate ids in the DOM, but passing the prop may
        // be redundant.
        id: props.id,
        inherits,
        label,
        ref: outerRef,
        subviews: subviews
    };
    const finalControlProps = isInSailForm ? componentProps : controlProps;
    return [
        fieldProps,
        finalControlProps
    ];
}
export const FormFieldSkeleton = ()=>/*#__PURE__*/ React.createElement(Skeleton, {
        css: {
            height: 'space.250',
            paddingX: 'small',
            paddingY: 'xsmall',
            borderRadius: 'small',
            boxSizing: 'content-box'
        }
    });

//# sourceMappingURL=FormField.js.map